import {
  isModuleEnabled,
  type ModuleId,
  type OrgModulesState,
} from "@/modules/registry";

/** Right-hand tabs of account and contact record views. */
export const PROFILE_TABS = [
  "pipeline",
  "timeline",
  "tasks",
  "notes",
  "emails",
  "calendar",
  "deals",
  "financial",
  "tickets",
  "files",
] as const;

export type ProfileTab = (typeof PROFILE_TABS)[number];

export const DEFAULT_PROFILE_TAB: ProfileTab = "timeline";

export const PROFILE_TAB_LABELS: Record<ProfileTab, string> = {
  pipeline: "Pipeline",
  timeline: "Timeline",
  tasks: "Tasks",
  notes: "Threads",
  emails: "Emails",
  calendar: "Calendar",
  deals: "Projects",
  financial: "Financial",
  tickets: "Tickets",
  files: "Files",
};

/** Older `?tab=` values and `?section=` deep links. */
const LEGACY_TABS: Record<string, ProfileTab> = {
  activity: "timeline",
  overview: "timeline",
  informacion: "timeline",
  people: "timeline",
  contacts: "timeline",
  referrals: "timeline",
  "web-forms": "timeline",
  meetings: "calendar",
  projects: "deals",
  proposals: "financial",
  contracts: "financial",
  threads: "notes",
};

/**
 * Resolve the tab from URL params, limited to the tabs the view offers.
 * `?section=tasks|notes` without a tab opens that tab (old Activity links).
 */
export const resolveProfileTab = (
  tab: string | null,
  section: string | null,
  available: readonly ProfileTab[],
  fallback: ProfileTab = DEFAULT_PROFILE_TAB,
): ProfileTab => {
  const pick = (value: ProfileTab) =>
    available.includes(value) ? value : fallback;
  if (!tab || tab === "activity") {
    if (section === "tasks") return pick("tasks");
    if (section === "notes") return pick("notes");
    if (!tab) return fallback;
  }
  if ((PROFILE_TABS as readonly string[]).includes(tab!))
    return pick(tab as ProfileTab);
  const legacy = LEGACY_TABS[tab!];
  return legacy ? pick(legacy) : fallback;
};

/** Financial deep links keep their accordion section. */
export const legacyFinancialSection = (tab: string | null) =>
  tab === "proposals" || tab === "contracts" ? tab : null;

/**
 * Module(s) a tab belongs to (Phase 3, docs/plans/MULTI-TENANT-MODULES.md):
 * a tab shows when the company has any of them on. Tabs not listed are core.
 */
export const PROFILE_TAB_MODULES: Partial<Record<ProfileTab, ModuleId[]>> = {
  pipeline: ["pipeline"],
  calendar: ["calendar"],
  deals: ["pipeline"],
  financial: ["invoices", "proposals", "contracts"],
  tickets: ["tickets"],
  emails: ["mail", "tickets"],
};

export const isProfileTabEnabled = (
  orgModules: OrgModulesState | null | undefined,
  tab: ProfileTab,
) => {
  const modules = PROFILE_TAB_MODULES[tab];
  return !modules || modules.some((id) => isModuleEnabled(orgModules, id));
};
